import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Linkedin, Instagram, Twitter, Facebook } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { adminLogin } from "../lib/api";

const Footer = () => {
  const { t, lang, setLang, languages } = useI18n();
  const navigate = useNavigate();
  const [showAdmin, setShowAdmin] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitAdmin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password);
      sessionStorage.setItem("mayak_admin", password);
      navigate("/admin");
    } catch {
      setError(t("admin.wrong"));
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.projects"), id: "projects" },
    { label: t("nav.whyUs"), id: "why" },
    { label: t("nav.contact"), id: "contact" }
  ];

  return (
    <footer
      className="bg-mayak-surface text-mayak-text border-t border-mayak-border"
      data-testid="footer-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <button
              onClick={() => scrollTo("hero")}
              className="flex items-center gap-2.5"
              data-testid="footer-logo"
            >
              <svg width="28" height="28" viewBox="0 0 28 28" className="text-mayak-accent">
                <path d="M14 3 L11 8 H17 L14 3 Z" fill="currentColor" />
                <rect x="11.5" y="8" width="5" height="2" fill="currentColor" />
                <path d="M11 10 L17 10 L18 24 L10 24 Z" stroke="currentColor" strokeWidth="1.4" fill="none" />
                <line x1="11" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1" />
                <line x1="10" y1="24" x2="18" y2="24" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="14" cy="6" r="1" fill="currentColor" />
              </svg>
              <span className="font-black text-2xl tracking-tight text-mayak-text">
                MAYAK
                <span className="text-mayak-accent">.</span>
              </span>
            </button>
            <p className="mt-6 text-mayak-dim max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <SocialLink Icon={Instagram} href="https://instagram.com" />
              <SocialLink Icon={Linkedin} href="https://linkedin.com" />
              <SocialLink Icon={Twitter} href="https://twitter.com" />
              <SocialLink Icon={Facebook} href="https://facebook.com" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-mayak-accent mb-6">
              {t("footer.navigate")}
            </h4>
            <ul className="space-y-3">
              {navItems.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => scrollTo(n.id)}
                    className="text-mayak-dim hover:text-mayak-text mayak-link-underline"
                    data-testid={`footer-link-${n.id}`}
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-mayak-accent mb-6">
              {t("footer.contactCol")}
            </h4>
            <div className="text-mayak-dim text-sm space-y-2">
              <div>{t("contact.info.email")}</div>
              <div>{t("contact.info.phone")}</div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-mayak-accent mb-6">
              {t("footer.languages")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 border text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    lang === l.code
                      ? "border-mayak-accent text-mayak-accent"
                      : "border-mayak-border-strong text-mayak-dim hover:border-mayak-text hover:text-mayak-text"
                  }`}
                  data-testid={`footer-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-mayak-border flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-mayak-faint">
          <span>© {new Date().getFullYear()} Mayak Construction. {t("footer.rights")}</span>
          <button
            onClick={() => setShowAdmin(true)}
            className="text-mayak-faint hover:text-mayak-accent uppercase tracking-[0.3em] mayak-link-underline"
            data-testid="admin-button"
          >
            {t("footer.admin")}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
            data-testid="admin-password-modal"
            onClick={() => setShowAdmin(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-mayak-surface border border-mayak-border p-10"
            >
              <h3 className="text-2xl font-black text-mayak-text mb-1">
                {t("admin.passwordPrompt")}
              </h3>
              <p className="text-xs text-mayak-faint uppercase tracking-[0.3em] mb-8">
                Mayak — Admin
              </p>
              <form onSubmit={submitAdmin}>
                <input
                  autoFocus
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("admin.passwordPlaceholder")}
                  className="mayak-input"
                  data-testid="admin-password-input"
                />
                {error && (
                  <p className="mt-3 text-sm text-red-500" data-testid="admin-password-error">
                    {error}
                  </p>
                )}
                <div className="mt-8 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdmin(false);
                      setPassword("");
                      setError("");
                    }}
                    className="ghost-btn !py-3 !px-5 !text-xs"
                    data-testid="admin-password-cancel"
                  >
                    {t("admin.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="magnetic-btn !py-3 !px-5 !text-xs"
                    data-testid="admin-password-submit"
                  >
                    {loading ? "…" : t("admin.submit")}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

const SocialLink = ({ Icon, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-10 h-10 border border-mayak-border-strong flex items-center justify-center text-mayak-dim hover:border-mayak-accent hover:text-mayak-accent transition-all"
  >
    <Icon size={16} strokeWidth={1.5} />
  </a>
);

export default Footer;
